import React, { useEffect } from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import NavigationHeader from './components/NavigationHeader';
import FooterWithPolicies from './components/FooterWithPolicies';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faFileContract } from '@fortawesome/free-solid-svg-icons';

const TermsOfService: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="policy-page">
      <NavigationHeader />
      
      <Container className="py-5">
        <Row>
          <Col lg={10} className="mx-auto">
            <Link to="/" className="btn btn-link mb-4">
              <FontAwesomeIcon icon={faArrowLeft} /> Back to Home
            </Link>
            
            <Card className="policy-content">
              <Card.Body className="p-5">
                <div className="text-center mb-5">
                  <FontAwesomeIcon icon={faFileContract} size="3x" className="text-primary mb-3" />
                  <h1>Terms of Service</h1>
                  <p className="text-muted">Last Updated: {new Date().toLocaleDateString()}</p>
                </div>

                <div className="policy-section">
                  <h2>Welcome to KairosIQ AI Realty</h2>
                  <p>
                    These Terms of Service ("Terms") govern your use of chat.realestate and 
                    associated services operated by KairosIQ AI Realty ("we," "us," or "our"), a 
                    licensed real estate brokerage. Our intelligent property search and matching 
                    technology is powered by KairosIQ AI, while all real estate services, 
                    brokerage relationships, and property listings are owned and managed by 
                    KairosIQ AI Realty. By using our services, you agree to these Terms. If you 
                    don't agree, please don't use our services.
                  </p>
                </div>

                <div className="policy-section">
                  <h3>1. Our Services</h3>
                  
                  <h4>What We Provide</h4>
                  <ul>
                    <li>Licensed real estate brokerage services through KairosIQ AI Realty</li>
                    <li>Exclusive access to MLS property listings owned by KairosIQ AI Realty</li>
                    <li>AI-powered property search and recommendations (KairosIQ technology)</li>
                    <li>Connection with KairosIQ AI Realty's licensed real estate agents</li>
                    <li>Property valuation estimates and market insights</li>
                    <li>Neighborhood information and analytics</li>
                    <li>Chat-based property search assistance powered by KairosIQ AI</li>
                  </ul>

                  <h4>What We Don't Provide</h4>
                  <ul>
                    <li>Legal advice or representation</li>
                    <li>Financial or mortgage advice (we only provide calculators)</li>
                    <li>Property inspections or warranties</li>
                    <li>Guaranteed accuracy of all listing information</li>
                  </ul>
                </div>

                <div className="policy-section">
                  <h3>2. Account Registration</h3>
                  <p>To access certain features, you may need to create an account. You agree to:</p>
                  <ul>
                    <li>Provide accurate and complete information</li>
                    <li>Keep your login credentials secure</li>
                    <li>Notify us of any unauthorized use</li>
                    <li>Be responsible for all activity under your account</li>
                  </ul>
                  <p>
                    We may suspend or terminate accounts that violate these Terms or for 
                    other legitimate business reasons.
                  </p>
                </div>

                <div className="policy-section">
                  <h3>3. Acceptable Use</h3>
                  
                  <h4>You May:</h4>
                  <ul>
                    <li>Search for properties for personal use</li>
                    <li>Save searches and favorite properties</li>
                    <li>Contact agents about listings</li>
                    <li>Use our tools for research and comparison</li>
                    <li>Share listings with others</li>
                  </ul>

                  <h4>You May Not:</h4>
                  <ul>
                    <li>Scrape, mine, or harvest data from our platform</li>
                    <li>Use automated systems or bots without permission</li>
                    <li>Misrepresent yourself or create fake accounts</li>
                    <li>Interfere with or disrupt our services</li>
                    <li>Use our services for illegal purposes</li>
                    <li>Harass agents or other users</li>
                    <li>Attempt to circumvent security measures</li>
                  </ul>
                </div>

                <div className="policy-section">
                  <h3>4. AI Technology Services and Limitations</h3>
                  
                  <p>KairosIQ AI technology enhances your property search experience but has limitations:</p>
                  <ul>
                    <li><strong>AI is a tool, not a broker</strong> - All real estate services are provided by KairosIQ AI Realty</li>
                    <li><strong>Recommendations are suggestions only</strong> - Always verify information with a licensed agent</li>
                    <li><strong>AI may make mistakes</strong> - Property matches and valuations are estimates</li>
                    <li><strong>Human oversight available</strong> - KairosIQ AI Realty agents oversee all transactions</li>
                    <li><strong>Learning from interactions</strong> - KairosIQ AI improves based on user feedback</li>
                  </ul>

                  <p>
                    <strong>Disclaimer:</strong> KairosIQ AI provides technology services only. 
                    All real estate decisions, transactions, and brokerage services are handled 
                    by KairosIQ AI Realty's licensed professionals. AI-generated content should not 
                    be your sole basis for real estate decisions.
                  </p>
                </div>

                <div className="policy-section">
                  <h3>5. Property Listings and Information</h3>
                  
                  <h4>Accuracy</h4>
                  <p>
                    KairosIQ AI Realty obtains listing information through its MLS memberships and 
                    brokerage relationships. While we strive for accuracy, we cannot guarantee 
                    all information is current or correct. Users should independently verify 
                    important details with a KairosIQ AI Realty agent.
                  </p>

                  <h4>Intellectual Property</h4>
                  <p>
                    Property listings, photos, and descriptions are owned by KairosIQ AI Realty 
                    and the listing brokers. The AI technology and algorithms are proprietary 
                    to KairosIQ. You may not use this content for commercial purposes without 
                    written permission from the respective owners.
                  </p>
                </div>

                <div className="policy-section">
                  <h3>6. Privacy and Data Use</h3>
                  <p>
                    Your privacy is important to us. Our use of your information is governed 
                    by our <Link to="/privacy-policy">Privacy Policy</Link>, which is incorporated 
                    into these Terms by reference.
                  </p>
                </div>

                <div className="policy-section">
                  <h3>7. Communications and SMS Consent</h3>
                  
                  <h4>Electronic Communications</h4>
                  <p>By using our services, you consent to receive:</p>
                  <ul>
                    <li>Service-related emails and notifications</li>
                    <li>Property alerts based on your saved searches (can opt-out)</li>
                    <li>Marketing communications (with your consent, can opt-out)</li>
                  </ul>

                  <h4>SMS/Text Messaging Terms (TCPA Compliance)</h4>
                  <p>
                    <strong>By providing your phone number, you expressly consent to receive text 
                    messages from KairosIQ AI Realty using an automatic telephone dialing system.</strong>
                  </p>
                  
                  <div className="sms-terms-box">
                    <h5>Text Messaging Terms:</h5>
                    <ul>
                      <li><strong>Frequency:</strong> Message frequency varies. You may receive up to 10 messages per month</li>
                      <li><strong>Charges:</strong> Message and data rates may apply</li>
                      <li><strong>Opt-Out:</strong> Reply STOP to any message to unsubscribe</li>
                      <li><strong>Help:</strong> Reply HELP for assistance or call 1-602-860-6110</li>
                      <li><strong>Privacy:</strong> We will not share your number with third parties for marketing</li>
                    </ul>
                    
                    <p className="mt-3">
                      <strong>Supported Carriers:</strong> AT&T, Verizon, T-Mobile, Sprint, Boost, 
                      Cricket, MetroPCS, U.S. Cellular, Virgin Mobile, and others. Carrier fees apply.
                    </p>
                    
                    <p>
                      <strong>Consent Not Required:</strong> Providing consent to receive text messages 
                      is not a condition of purchasing any goods or services from KairosIQ AI Realty.
                    </p>
                  </div>
                </div>

                <div className="policy-section">
                  <h3>8. Fees and Payments</h3>
                  <p>
                    Our basic services are free for consumers. Premium features, if offered, 
                    will be clearly marked with pricing. Any fees are non-refundable unless 
                    otherwise stated.
                  </p>
                </div>

                <div className="policy-section">
                  <h3>9. Third-Party Services</h3>
                  <p>
                    Our platform may link to third-party services (lenders, inspectors, etc.). 
                    We are not responsible for their content or practices. Your use of 
                    third-party services is at your own risk.
                  </p>
                </div>

                <div className="policy-section">
                  <h3>10. Disclaimers and Limitations</h3>
                  
                  <h4>No Warranties</h4>
                  <p>
                    OUR SERVICES ARE PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND, EXPRESS 
                    OR IMPLIED. WE DON'T GUARANTEE CONTINUOUS, ERROR-FREE, OR SECURE ACCESS 
                    TO OUR SERVICES.
                  </p>

                  <h4>Limitation of Liability</h4>
                  <p>
                    TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY 
                    INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES ARISING FROM YOUR 
                    USE OF OUR SERVICES.
                  </p>

                  <h4>Indemnification</h4>
                  <p>
                    You agree to indemnify and hold us harmless from any claims arising from 
                    your use of our services or violation of these Terms.
                  </p>
                </div>

                <div className="policy-section">
                  <h3>11. Fair Housing</h3>
                  <p>
                    We are committed to fair housing for all. We comply with federal, state, 
                    and local fair housing laws. Discrimination based on race, color, religion, 
                    sex, national origin, disability, or familial status is strictly prohibited.
                  </p>
                </div>

                <div className="policy-section">
                  <h3>12. Dispute Resolution</h3>
                  
                  <h4>Informal Resolution</h4>
                  <p>
                    We prefer to resolve disputes informally. Contact us at 
                    <a href="mailto:support@connectrealty.com"> support@connectrealty.com</a> to 
                    discuss any concerns.
                  </p>

                  <h4>Arbitration</h4>
                  <p>
                    Any disputes not resolved informally shall be settled by binding arbitration 
                    in accordance with the rules of the American Arbitration Association. 
                    Arbitration will be conducted in Arizona.
                  </p>

                  <h4>Class Action Waiver</h4>
                  <p>
                    You agree to resolve disputes individually and waive any right to 
                    participate in class actions.
                  </p>
                </div>

                <div className="policy-section">
                  <h3>13. Changes to Terms</h3>
                  <p>
                    We may update these Terms periodically. Material changes will be 
                    communicated via email or prominent website notice. Continued use 
                    after changes constitutes acceptance.
                  </p>
                </div>

                <div className="policy-section">
                  <h3>14. Termination</h3>
                  <p>
                    You may stop using our services at any time. We may suspend or 
                    terminate your access for violations of these Terms or other reasons. 
                    Upon termination, your right to use our services ends immediately.
                  </p>
                </div>

                <div className="policy-section">
                  <h3>15. General Provisions</h3>
                  <ul>
                    <li><strong>Governing Law:</strong> These Terms are governed by Arizona law</li>
                    <li><strong>Entire Agreement:</strong> These Terms constitute the entire agreement between us</li>
                    <li><strong>Severability:</strong> If any provision is invalid, the rest remain in effect</li>
                    <li><strong>No Waiver:</strong> Our failure to enforce any right is not a waiver</li>
                    <li><strong>Assignment:</strong> We may assign these Terms; you may not without our consent</li>
                  </ul>
                </div>

                <div className="policy-section">
                  <h3>16. Contact Information</h3>
                  <p>For questions about these Terms, contact us:</p>
                  <ul className="list-unstyled">
                    <li><strong>Email:</strong> <a href="mailto:legal@connectrealty.com">legal@connectrealty.com</a></li>
                    <li><strong>Phone:</strong> 1-602-860-6110</li>
                    <li><strong>Mail:</strong> KairosIQ AI Realty, Legal Department, 410 N. Scottsdale Rd, Suite 10-160, Tempe, AZ 85281</li>
                  </ul>
                </div>

                <div className="text-center mt-5">
                  <Link to="/privacy-policy" className="btn btn-outline-primary">
                    View Privacy Policy
                  </Link>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>

      <FooterWithPolicies />

      <style>{`
        .policy-page {
          background-color: #f8f9fa;
          min-height: 100vh;
        }

        .policy-content {
          box-shadow: 0 0 20px rgba(0,0,0,0.1);
          border: none;
        }

        .policy-section {
          margin-bottom: 40px;
        }

        .policy-section h2 {
          color: #333;
          margin-bottom: 20px;
          font-size: 28px;
        }

        .policy-section h3 {
          color: #333;
          margin-bottom: 15px;
          margin-top: 30px;
          font-size: 22px;
        }

        .policy-section h4 {
          color: #555;
          margin-bottom: 10px;
          margin-top: 20px;
          font-size: 18px;
        }

        .policy-section p {
          line-height: 1.7;
          color: #555;
        }

        .policy-section ul {
          line-height: 1.8;
          color: #555;
        }

        .policy-section ul li {
          margin-bottom: 8px;
        }

        .sms-terms-box {
          background-color: #e8f4f8;
          border: 1px solid #bee5eb;
          border-radius: 8px;
          padding: 20px;
          margin: 20px 0;
        }

        .sms-terms-box h5 {
          color: #004085;
          margin-bottom: 15px;
        }
      `}</style>
    </div>
  );
};

export default TermsOfService;